import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { OCCASIONS, MORE_OCCASIONS, IMAGES } from "@/lib/content";
import { SectionHeading } from "./Primitives";

const Detail = ({ current }) => (
  <div className="border-l border-sec/60 pl-6 sm:pl-8 min-h-[200px]" aria-live="polite" data-testid="occasion-detail">
    {current ? (
      <div key={current.id} className="reveal">
        <h3 className="h-sub">{current.label}</h3>
        <p className="mt-4 text-base leading-relaxed text-ink">{current.detail}</p>
        <Link to={`/book?reason=${current.id}`} className="link-arrow mt-6" data-testid="occasion-book-link">
          Book with this reason <ArrowRight size={16} />
        </Link>
      </div>
    ) : (
      <div>
        <h3 className="h-sub">Any time is a reasonable time to measure.</h3>
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
          <dt className="mono text-sec pt-0.5">Visit</dt><dd className="text-ink">One visit: tap samples, on-site readings, monitor set-up.</dd>
          <dt className="mono text-sec pt-0.5">Monitor</dt><dd className="text-ink">Seven days in the bedroom, through real nights and ordinary days.</dd>
          <dt className="mono text-sec pt-0.5">Lab</dt><dd className="text-ink">Water analyzed by a laboratory; results reported with EPA reference levels.</dd>
          <dt className="mono text-sec pt-0.5">Review</dt><dd className="text-ink">Written findings plus a 45-minute call to decide what to do next.</dd>
        </dl>
      </div>
    )}
  </div>
);

export const WhenToBook = () => {
  const [active, setActive] = useState(null);
  const current = OCCASIONS.find((o) => o.id === active);
  return (
    <section className="bg-white py-16 sm:py-28 rule" id="when-to-book" data-testid="occasions-section">
      <div className="container-x">
        <SectionHeading
          title={<>A new home. <br className="sm:hidden" />A growing family.<br /> A reason to know.</>}
          lead="People book for different reasons. Choose the one closest to yours, if you like, and we’ll carry it into your booking."
          testId="occasions-heading"
          editorial
        />
        <div className="mt-10 sm:mt-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="flex flex-wrap gap-2.5" role="group" aria-label="Choose an occasion" data-testid="occasion-chips">
              {OCCASIONS.map((o) => {
                const on = o.id === active;
                return (
                  <button key={o.id} type="button" aria-pressed={on} onClick={() => setActive(on ? null : o.id)} data-testid={`occasion-chip-${o.id}`} className={`chip ${on ? "chip-active" : "chip-idle"}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-sec leading-relaxed max-w-md">Also common: {MORE_OCCASIONS.join(", ").toLowerCase()}.</p>
            <figure className="mt-8 aspect-[16/10] overflow-hidden rounded-md hidden lg:block">
              <img src={IMAGES.bedroom} alt="Calm bedroom with a soft upholstered bed, linen bedding and a bedside table in warm daylight" loading="lazy" className="h-full w-full object-cover" />
            </figure>
          </div>
          <div className="lg:col-span-6"><Detail current={current} /></div>
        </div>
      </div>
    </section>
  );
};
